import { Component, computed, input, OnInit, output, signal } from '@angular/core';
import { Router } from '@angular/router';
import { ServiceCatalogRedirection, ServiceProviderContact, ServiceProviderInfo, ServiceProviderPayload } from 'src/app/models/request-service.model';
import { RequestServicePage } from 'src/app/pages/request-service/request-service.page';
import { AccidentDamageService } from 'src/app/services/accident-damage/accident-damage.service';
import { CoreService } from 'src/app/services/core/core.service';
import { RequestServiceProvider } from 'src/app/services/providers/request-service-provider/request-service-provider';
import { VehicleProvider } from 'src/app/services/providers/vehicle-provider/vehicle-provider';
import { RequestBookingService } from 'src/app/services/request-booking/request-booking.service';

@Component({
  selector: 'app-service-provider',
  templateUrl: './service-provider.component.html',
  styleUrls: ['./service-provider.component.scss'],
  standalone: false
})
export class ServiceProviderComponent {

  serviceProviderData = input<ServiceCatalogRedirection | null>();
  icnNo = input.required<number>();

  providerDetails = signal<ServiceProviderInfo | null>(null);
  contactDetails = signal<ServiceProviderContact[]>([]);
  contactDetailsCount = signal<number>(0);
  isMobileApp = signal<boolean>(false);
  isCallHolman = input<boolean>(false);
  sortedContactDetails = computed(() => {
    return [...this.contactDetails()].sort((a, b) => {
      if (a.contacT_TYPE_KEY === 'PHONE') return -1;
      if (b.contacT_TYPE_KEY === 'PHONE') return 1;
      return 0;
    });
  });

  fastFit = output<ServiceProviderInfo>();
  valueChange = output<void>();

  constructor(
    private readonly vehicleProvider: VehicleProvider,
    private readonly coreService: CoreService,
    private readonly accidentDamageService: AccidentDamageService,
    private readonly router: Router,
    private readonly requestBookingService: RequestBookingService,
    private readonly requestServiceProvider: RequestServiceProvider,
    private readonly requestBookingPage: RequestServicePage

  ) { }

  /**
   * Angular lifecycle hook used to initialize platform state and fetch provider data.
   */
  ngOnInit() {
    this.isMobileApp.set(this.coreService.isMobileApp);
    this.getProviderData();
  }

  /**
   * Fetches service provider details and contact information based on ICN and provider key.
   */
  getProviderData() {

    let serviceProData: ServiceProviderPayload = {
      icnNo: this.icnNo(),
      providerId: this.isCallHolman() ? 'HOLMAN' : (this.serviceProviderData()?.provideR_KEY ?? '')
    }

    this.requestBookingService.getServiceProvider(serviceProData).subscribe({
      next: (response) => {
        if (response && response.serviceProviderInfo) {
          this.providerDetails.set(response.serviceProviderInfo);
          this.contactDetails.set(response.serviceProviderContact);
          this.contactDetailsCount.set(response.serviceProviderContact.length);
        }
      }
    });
  }

  /**
   * Handles action button clicks and routes flow based on provider type key.
   */
  handleActionButton(data: ServiceProviderContact) {
    const provider_key = this.serviceProviderData()?.provideR_KEY;

    if (provider_key == 'SOPP_SOPP') {
      this.openAccidentDamage();
    }
    else if (provider_key == 'HALFORDS_WEFIT' && this.providerDetails()) {
      this.fastFit.emit(this.providerDetails()!);
    }
    else if (provider_key == 'HOLMAN') {
      this.router.navigateByUrl('/appointments');
    } else if (provider_key != null) {

      try {
        const url = new URL(data.contacT_VALUE, globalThis.location.origin);
        if (url.protocol === 'http:' || url.protocol === 'https:') {
          window.open(url.toString(), '_blank', 'noopener,noreferrer');
        }
      } catch (error) {
        throw error;
      }

    }

  }

  /**
   * Triggers accident damage reporting flow with current vehicle and platform context.
   */
  openAccidentDamage() {
    this.accidentDamageService.onReportAccidentDamange(this.icnNo(), this.isMobileApp());
  }

  /**
   * Emits back navigation event to parent component.
   */
  backBtnClicked() {
    if (this.isCallHolman()) {
      this.router.navigateByUrl('/pre-use-inspection');
      return;
    }

    const concernData = this.requestServiceProvider.readConcernData();
    if (concernData?.navigateHistory && concernData?.navigateHistory.length > 1) {
      this.requestBookingPage.getServiceCatalog(concernData.navigateHistory[concernData.navigateHistory.length - 1]);
    }
    else {
      this.valueChange.emit();
      this.router.navigateByUrl('/request-booking');
    }
  }

  /**
   * Initiates phone call to Holman only when running inside mobile app environment.
   */
  callHolman(number: string) {
    if (!this.isMobileApp()) {
      return;
    }
    this.coreService.callHolman(number);
  }

  /**
   * Generates base64 image URL for rendering provider logo.
   */
  getImageUrl(logo: string, mimeType: string): string {
    if (!logo || !mimeType) {
      return '';
    }

    if (mimeType.trim().toLowerCase() === 'image/png') {
      return ('data:image/png;base64,' + logo);
    }
    else if (mimeType.trim().toLowerCase() === 'image/svg+xml') {
      // Provider SVG payloads are Base64-encoded UTF-16LE text (not raw UTF-8 bytes),
      // so it must be decoded to a string first, then re-encoded as UTF-8 for the data URI.
      return ('data:image/svg+xml;base64,' + this.reEncodeSvgAsUtf8(logo));
    } else {
      return ('data:image/jpg;base64,' + logo);
    }
  }

  /**
   * Converts a Base64 UTF-16LE encoded SVG payload into a Base64 UTF-8 encoded payload
   * so browsers can correctly parse it as SVG markup.
   */
  private reEncodeSvgAsUtf8(logo: string): string {
    try {
      const binary = atob(logo);
      const bytes = new Uint8Array(binary.length);
      for (let i = 0; i < binary.length; i++) {
        bytes[i] = binary.charCodeAt(i);
      }
      const svgText = new TextDecoder('utf-16le').decode(bytes);
      const utf8Bytes = new TextEncoder().encode(svgText);
      let utf8Binary = '';
      utf8Bytes.forEach(byte => utf8Binary += String.fromCharCode(byte));
      return btoa(utf8Binary);
    } catch {
      return logo;
    }
  }

  /* Opens the default mail client for the provided email address */
  openMail(email: string | undefined): void {
    if (email) {
      globalThis.location.href = `mailto:${email}`;
    }
  }
}
